/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Low-level reCAPTCHA execution: enterprise validation, timed execution, and
 * token caching.
 */

import { AntiBotUnavailableError } from './recaptcha-loader';
import { getGrecaptchaEnterprise, GRecaptchaEnterprise } from './recaptcha-config';
import { reportRecaptchaToInspector } from './recaptcha-devcockpit';

export const ANTIBOT_LOG = '[ANTIBOT]';
export const EXECUTE_TIMEOUT_MS = 8_000;
export const TOKEN_CACHE_TTL_MS = 60_000;

/**
 * Ensures reCAPTCHA Enterprise is initialized, returning the handle or `null`.
 * Reports an inspector error when initialization has failed.
 *
 * @param siteKey Site key used for inspector reporting on failure.
 * @returns The Enterprise handle, or `null` when not initialized.
 */
export function checkRecaptchaEnterprise(siteKey: string): ReturnType<typeof getGrecaptchaEnterprise> {
  const enterprise = getGrecaptchaEnterprise();
  if (enterprise) return enterprise;

  const err = new Error('reCAPTCHA Enterprise failed to initialize');
  if (siteKey) reportRecaptchaToInspector(siteKey, 0, err.message);
  return null;
}

/**
 * Execute reCAPTCHA inside the `ready()` callback for guaranteed initialization.
 *
 * The `ready()` callback is the ONLY mechanism Google provides to ensure the
 * SDK has completed internal initialization (network handshake, domain check).
 * Calling `execute()` outside `ready()` causes intermittent BROWSER_ERROR tokens.
 *
 * Includes a safety timeout: if `ready()` never fires its callback (e.g. due to
 * internal domain validation failure), the promise rejects after EXECUTE_TIMEOUT_MS
 * instead of hanging indefinitely.
 *
 * @param enterprise Initialized reCAPTCHA Enterprise handle.
 * @param siteKey Site key to execute against.
 * @param action Action name recorded with the token.
 * @returns A promise resolving to the generated token.
 * @throws {AntiBotUnavailableError} When execution times out.
 */
export function executeInsideReady(
  enterprise: GRecaptchaEnterprise,
  siteKey: string,
  action: string,
): Promise<string> {
  const executePromise = new Promise<string>((resolve, reject) => {
    enterprise.ready(async () => {
      try {
        const token = await enterprise.execute(siteKey, { action });
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new AntiBotUnavailableError(
        `reCAPTCHA execute timed out after ${EXECUTE_TIMEOUT_MS}ms (ready callback may not have fired)`,
      ));
    }, EXECUTE_TIMEOUT_MS);
  });

  return Promise.race([executePromise, timeoutPromise]);
}

interface CachedToken {
  value: string;
  timestamp: number;
  action: string;
}

let _tokenCache: CachedToken | null = null;

/**
 * Reports whether the cached token is still valid for the given action and
 * within its TTL window.
 *
 * @param action Action the cached token must match.
 * @returns `true` when a valid, matching cached token exists.
 */
export function isCacheValid(action: string): boolean {
  if (!_tokenCache) return false;
  if (_tokenCache.action !== action) return false;
  return (Date.now() - _tokenCache.timestamp) < TOKEN_CACHE_TTL_MS;
}

/**
 * @returns The cached token value (call only after {@link isCacheValid}).
 */
export function getCachedTokenValue(): string {
  return _tokenCache!.value;
}

/**
 * Stores a freshly generated token in the cache with the current timestamp.
 *
 * @param value The token value to cache.
 * @param action The action the token was generated for.
 */
export function setCachedToken(value: string, action: string): void {
  _tokenCache = { value, timestamp: Date.now(), action };
}

/**
 * Invalidate the cached anti-bot token, forcing the next call to
 * generateAntiBotToken() to request a fresh one from the SDK.
 */
export function invalidateAntiBotTokenCache(): void {
  _tokenCache = null;
}
